import React, { useState } from 'react';
import { Phone, Mail, MapPin, Star, Check, ChevronRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { tours, pickupLocations } from '../mockData';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConciergePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    hotelName: '',
    email: '',
    phone: '',
    position: '',
    monthlyReferrals: '',
    preferredContact: 'Email',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/concierge-registration`, formData);
      
      toast({
        title: "Registration Successful!",
        description: "Thank you! Our partnership team will contact you within 24 hours.",
      });
      
      // Reset form
      setFormData({
        name: '',
        hotelName: '',
        email: '',
        phone: '',
        position: '',
        monthlyReferrals: '',
        preferredContact: 'Email',
        notes: ''
      });
      
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Registration Received",
        description: "Your registration has been saved. We'll contact you soon!",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-gray-950 text-white py-4 px-4 shadow-lg border-b border-yellow-600/20">
        <div className="container mx-auto flex items-center justify-between">
          <Button 
            variant="ghost" 
            className="text-white hover:bg-white/20"
            onClick={() => navigate('/')}
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back to Main Site
          </Button>
          <div className="flex items-center gap-6">
            <a href="tel:+18777418390" className="flex items-center gap-2 hover:underline">
              <Phone className="h-4 w-4" />
              <span className="hidden md:inline">(877) 741-8390</span>
            </a>
            <a href="mailto:info@officialhollywoodtours.com" className="flex items-center gap-2 hover:underline">
              <Mail className="h-4 w-4" />
              <span className="hidden md:inline">info@officialhollywoodtours.com</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <Badge className="mb-4 text-lg px-6 py-2 bg-yellow-500 text-black hover:bg-yellow-400">
            Partner Program
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Hollywood Tours Concierge Partner Program
          </h1>
          <p className="text-xl md:text-2xl mb-8 font-light">
            Join our exclusive partner network. Receive VIP perks and give your guests access to the #1 rated Hollywood sightseeing tours, Walk of Fame experiences, and Hollywood attractions.
          </p>
          <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <Star className="h-12 w-12 mx-auto mb-3 text-yellow-400" />
                <h3 className="font-bold text-lg mb-2">VIP Partner Status</h3>
                <p className="text-sm">Exclusive benefits & priority booking</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <Check className="h-12 w-12 mx-auto mb-3 text-green-400" />
                <h3 className="font-bold text-lg mb-2">Exclusive Perks</h3>
                <p className="text-sm">Commission on every referral</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
              <CardContent className="p-6 text-center">
                <Phone className="h-12 w-12 mx-auto mb-3 text-blue-400" />
                <h3 className="font-bold text-lg mb-2">24/7 Support</h3>
                <p className="text-sm">Dedicated partnership team</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="concierge-form" className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Concierge <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Registration</span>
            </h2>
            <p className="text-xl text-gray-600">
              Fill out the form below and our partnership team will contact you within 24 hours
            </p>
          </div>

          <Card className="border border-yellow-500/30 shadow-2xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Hotel Name *
                    </label>
                    <Input
                      type="text"
                      name="hotelName"
                      value={formData.hotelName}
                      onChange={handleInputChange}
                      required
                      placeholder="Hollywood Hotel"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="john@hotel.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Phone *
                    </label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Position *
                    </label>
                    <Input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      placeholder="Concierge Manager"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Est. Monthly Referrals *
                    </label>
                    <select
                      name="monthlyReferrals"
                      value={formData.monthlyReferrals}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select range</option>
                      <option value="1-5">1-5 per month</option>
                      <option value="5-15">5-15 per month</option>
                      <option value="15-30">15-30 per month</option>
                      <option value="30+">30+ per month</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <div className="flex gap-4">
                    {['Email', 'Phone', 'Text/SMS'].map((method) => (
                      <label key={method} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="preferredContact"
                          value={method}
                          checked={formData.preferredContact === method}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-red-600"
                        />
                        <span>{method}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Additional Notes
                  </label>
                  <Textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    placeholder="Any additional information..."
                    rows={4}
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-yellow-600 to-yellow-500 hover:from-yellow-500 hover:to-yellow-400 text-black font-bold py-6 text-lg disabled:opacity-50"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                  {!isSubmitting && <ChevronRight className="ml-2 h-5 w-5" />}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* All Tours */}
      <section id="tours" className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              All Hollywood Sightseeing Tours & <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Experiences</span>
            </h2>
            <p className="text-xl text-gray-600">
              Recommend these incredible Hollywood tours and attractions to your guests — from Walk of Fame sightseeing to nightlife experiences
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tours.map((tour) => (
              <Card key={tour.id} className="hover:shadow-xl transition-shadow">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={tour.image} 
                    alt={tour.title}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-xl">{tour.title}</CardTitle>
                  <p className="text-gray-600">{tour.subtitle}</p>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <p className="text-3xl font-black text-yellow-600">${tour.price}</p>
                    <p className="text-sm text-gray-500">{tour.duration}</p>
                  </div>
                  <ul className="space-y-1 text-sm">
                    {tour.includes.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hotel Pickup Directory */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black mb-4 text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Hollywood Tour Hotel <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-600 to-yellow-500">Pickup Directory</span>
            </h2>
            <p className="text-xl text-gray-600">
              We pick up from major hotels across Hollywood, Century City, Downtown & Santa Monica for all Hollywood sightseeing tours
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pickupLocations.map((location) => (
              <Card key={location.area}>
                <CardHeader className="bg-gradient-to-br from-yellow-600 to-yellow-500 text-black">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    <CardTitle className="text-lg">{location.area}</CardTitle>
                  </div>
                  <Badge className="bg-black/20 text-black mt-2">
                    {location.hotels.length}+ hotels
                  </Badge>
                </CardHeader>
                <CardContent className="pt-6">
                  <ul className="space-y-2 text-sm">
                    {location.hotels.map((hotel, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-yellow-600 font-bold">•</span>
                        <span className="text-gray-700">{hotel}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Card className="bg-gradient-to-r from-yellow-600 to-yellow-500 border-none text-black inline-block">
              <CardContent className="p-6">
                <p className="text-lg font-bold mb-2">Don't see your hotel?</p>
                <p>We can arrange pickup from any location in the area.</p>
                <Button 
                  className="mt-4 bg-black text-yellow-500 hover:bg-gray-900"
                  onClick={() => window.location.href = 'tel:+18777418390'}
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call 24/7: (877) 741-8390
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl text-center">
          <p className="text-gray-400 mb-2">
            Part of{' '}
            <a 
              href="https://officialhollywoodtours.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-400 hover:underline"
            >
              Official Hollywood Tours LLC
            </a>
          </p>
          <p className="text-sm text-gray-500">
            &copy; 2026 Hollywood Fame Tour. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ConciergePage;
